import { useMemo, useRef, useEffect } from 'react';
import { useStableCallback } from '@olxpwa-lib/utils';
import { useHostProps } from '~features/host-props';
import { useCompanyProfileEmployerSegmentQuery } from '~api';
import type { TrackFnOptions, DefaultEventParams, EventParams, TouchPointPage } from './types';
import { type TrackEventFn, type TrackPageViewFn, TrackingContext } from './tracking-context';

type Props = React.PropsWithChildren<{
  employerUserId?: string | null;
  loading: boolean;
  forceTouchPointPage?: TouchPointPage;
}>;

export const TrackingProvider = ({
  employerUserId,
  loading,
  children,
  forceTouchPointPage,
}: Props) => {
  const initialEventsQueue = useRef<Parameters<TrackEventFn>[]>([]);
  const initialPageViewsQueue = useRef<Parameters<TrackPageViewFn>[]>([]);
  const {
    trackEvent: baseTrackEvent,
    trackPageView: baseTrackPageView,
    user: { userId } = {},
  } = useHostProps();
  const { data, loading: segmentLoading } = useCompanyProfileEmployerSegmentQuery({
    variables: {
      atlasUserId: employerUserId as string,
    },
    skip: !employerUserId,
  });
  const { employerSegment } = data?.CompanyProfile ?? {};
  const defaultParams = useMemo<DefaultEventParams | null>(() => {
    if (loading || segmentLoading) {
      return null;
    }

    const userIdAsString = userId?.toString();
    // Atlas user id can't exist for unpublished profile, so if it's falsy it means it's an employer side
    // This condition will also handle case when logged in employer visits her/his own profile via public route
    let userStatus: EventParams['jobs_user_status'] = 'seeker';

    if (employerUserId && userIdAsString === employerUserId) {
      userStatus = 'employer';
    }

    return {
      jobs_user_status: userStatus,
      ...(forceTouchPointPage && { touch_point_page: forceTouchPointPage }),
      ...(userIdAsString && { user_id: userIdAsString }),
      ...(employerSegment && { employer_segment: employerSegment.type }),
      ...(employerUserId && { seller_id: employerUserId }),
    };
  }, [userId, loading, segmentLoading, employerUserId, employerSegment, forceTouchPointPage]);

  const trackEvent = useStableCallback<Parameters<TrackEventFn>, void>((...args) => {
    if (defaultParams === null) {
      initialEventsQueue.current.push(args);

      return;
    }

    const [name, options] = args;
    const { params, touchPointButton } = (options as TrackFnOptions | undefined) ?? {};

    baseTrackEvent(
      name,
      {
        ...defaultParams,
        ...params,
      },
      touchPointButton,
    );
  });

  const trackPageView = useStableCallback<Parameters<TrackPageViewFn>, void>((...args) => {
    if (defaultParams === null) {
      initialPageViewsQueue.current.push(args);

      return;
    }

    const [name, options] = args;
    const { params, touchPointButton } = (options as TrackFnOptions | undefined) ?? {};

    baseTrackPageView(
      name,
      {
        ...defaultParams,
        ...(touchPointButton && { touch_point_button: touchPointButton }),
        ...params,
      },
      undefined,
      undefined,
      true,
    );
  });

  const value = useMemo<React.ContextType<typeof TrackingContext>>(
    () => ({
      trackEvent,
      trackPageView,
    }),
    [trackEvent, trackPageView],
  );

  useEffect(() => {
    if (defaultParams === null) {
      return;
    }

    while (initialPageViewsQueue.current.length > 0) {
      const params = initialPageViewsQueue.current.pop();

      if (params) {
        trackPageView(...params);
      }
    }

    while (initialEventsQueue.current.length > 0) {
      const params = initialEventsQueue.current.pop();

      if (params) {
        trackEvent(...params);
      }
    }
  }, [defaultParams, trackEvent, trackPageView]);

  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
};
